/**
 * One row of the group switcher: crown (its master config's color) and the
 * group's label, what it holds on the second line, its member names on
 * hover, and the pin — or the "All campaign groups" row.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Check, Crown, Layers, Pin } from 'lucide-react';

import { cn } from '@/lib/utils';

import { entryFacts, type GroupEntry } from './campaign-scope';

interface Props {
  entry?: GroupEntry;
  all?: boolean;
  total?: number;
  current: string | null;
  canPin: boolean;
  onSelect: (key: string | null) => void;
  onPin: (entry: GroupEntry, on: boolean) => void;
}

export const SwitcherRow: FC<Props> = ({ entry, all, total = 0, current, canPin, onSelect, onPin }) => {
  const key = all || !entry ? null : entry.key;
  const active = current === key;
  return (
    <div className={cn('group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent', active && 'bg-primary/5')}>
      <button type="button" onClick={() => onSelect(key)} title={entry?.fullLabel} className="flex min-w-0 flex-1 items-start gap-2 text-left">
        <span className="mt-0.5 w-4 shrink-0 text-primary">{active ? <Check className="h-4 w-4" /> : null}</span>
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
            {all ? <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : null}
            {entry?.master ? <Crown className="h-3.5 w-3.5 shrink-0" style={{ color: entry.master.color }} /> : null}
            <span className="truncate">{all ? 'All campaign groups' : entry?.label}</span>
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {all || !entry ? `every container of the country · ${total}` : entryFacts(entry)}
          </span>
        </span>
      </button>
      {entry && canPin ? (
        <button
          type="button"
          onClick={() => onPin(entry, !entry.pinned)}
          aria-label={entry.pinned ? `Unpin ${entry.label}` : `Pin ${entry.label}`}
          title={entry.pinned ? 'Pinned — click to unpin' : 'Pin it to the top of this list (also pins its campaigns on the SMSWiser page)'}
          className={cn(
            'shrink-0 rounded p-1.5',
            entry.pinned ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100',
          )}
        >
          <Pin className={cn('h-3.5 w-3.5', entry.pinned && 'fill-current')} />
        </button>
      ) : null}
    </div>
  );
};
